import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Lock, AlertCircle, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import usersService from "../../services/usersService";
import { formatError } from "../../utils/errorHandler";
import { validationRules, getPasswordStrength } from "../../utils/validation";
import { useTranslation } from "../../i18n";

const ChangePassword = () => {
  const { t } = useTranslation("settings");
  const rules = validationRules(t);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: ""
    },
  });

  const newPassword = watch("new_password");
  const passwordStrength = getPasswordStrength(newPassword);
  const strengthLabel = {
    weak: t("password.strength.weak"),
    medium: t("password.strength.medium"),
    strong: t("password.strength.strong"),
  };

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data) => usersService.changePassword(data.current_password, data.new_password),
    onSuccess: () => {
      setSuccess(true);
      setError("");
      reset();
      setTimeout(() => setSuccess(false), 3000);
    },
    onError: (err) => {
      setError(formatError(err));
      setSuccess(false);
    },
  });

  const onSubmit = (data) => {
    changePasswordMutation.mutate(data);
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">{t("password.title")}</h2>

      {/* Success Message */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 bg-green-50 border border-green-200 rounded-2xl flex items-center text-green-700"
          >
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <p className="text-sm">{t("password.updated")}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Current Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("password.current")}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="password"
              {...register("current_password", rules.password)}
              placeholder="••••••••"
              className={`w-full pl-10 pr-3 py-2 border rounded-full focus:outline-none focus:ring-2 ${
                errors.current_password
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-purple-500"
              }`}
            />
          </div>
          {errors.current_password && (
            <p className="mt-1 text-xs text-red-600">{errors.current_password.message}</p>
          )}
        </div>

        {/* New Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("password.new")}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="password"
              {...register("new_password", rules.password)}
              placeholder="••••••••"
              className={`w-full pl-10 pr-3 py-2 border rounded-full focus:outline-none focus:ring-2 ${
                errors.new_password
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-purple-500"
              }`}
            />
          </div>
          {errors.new_password && (
            <p className="mt-1 text-xs text-red-600">{errors.new_password.message}</p>
          )}
          {/* Password Strength Indicator */}
          {newPassword && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      passwordStrength === 'weak' ? 'bg-red-500 w-1/3' :
                      passwordStrength === 'medium' ? 'bg-yellow-500 w-2/3' :
                      'bg-green-500 w-full'
                    }`}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  passwordStrength === 'weak' ? 'text-red-600' :
                  passwordStrength === 'medium' ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {strengthLabel[passwordStrength] || passwordStrength}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("password.confirm")}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="password"
              {...register("confirm_password", rules.confirmPassword(newPassword))}
              placeholder="••••••••"
              className={`w-full pl-10 pr-3 py-2 border rounded-full focus:outline-none focus:ring-2 ${
                errors.confirm_password
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-purple-500"
              }`}
            />
          </div>
          {errors.confirm_password && (
            <p className="mt-1 text-xs text-red-600">{errors.confirm_password.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={changePasswordMutation.isLoading}
          className="px-4 py-2 org-gradient text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {changePasswordMutation.isLoading ? t("common:actions.saving") : t("password.submit")}
        </button>
      </form>
    </div>
  );
};

export default ChangePassword;
